import { useState } from "react";
import { controlToastState, Toast } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../api/firebase/references";
import { createPortalSecret } from "../../../../api/postCall/createPortalSecret";
import { fetchPortalPassword } from "../../../../api/postCall/fetchPortalPassword";
import {
  decryptPassword,
  encryptPassword,
} from "../../../../utils/SimplePasswordCrypto";
import AddEmailPasswordModal from "./components/addEmailPasswordModal";
import EmailPasswordCard from "./components/emailPasswordCard";
import Header from "./components/header";
import {
  EmailConfiguration,
  useEmailConfigurations,
} from "./hooks/useEmailConfigurations";
import {
  addModeField1,
  addModeField2,
  editModeField2,
  getEditModeField1,
} from "./utils/fieldConfigs";

type ModalMode = "add" | "edit";

const EmailPasswordConfiguration = () => {
  const { emailConfigurations, loading, error, refetch } =
    useEmailConfigurations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [selectedConfig, setSelectedConfig] =
    useState<EmailConfiguration | null>(null);

  const handleAddClick = () => {
    setModalMode("add");
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: EmailConfiguration) => {
    setModalMode("edit");
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedConfig(null);
  };

  const handleSave = async (formData: Record<string, string>) => {
    if (modalMode === "add") {
      const email = formData.email;
      const secretId = formData.secretId;

      if (!email || !secretId) {
        throw new Error("Email and Secret ID are required");
      }

      await FirestoreService.addDocument(
        FirestoreCollectionReference.emailOptions(),
        {
          email,
          secret_id: secretId,
        },
      );
    } else {
      const secretId = formData.secretId;
      const appPassword = formData.appPassword;

      if (!secretId || !appPassword) {
        throw new Error("Secret ID and App Password are required");
      }

      const encryptedPassword = await encryptPassword(appPassword);

      await createPortalSecret({
        secret_id: secretId,
        secret_value: encryptedPassword,
      });
    }
    refetch();
  };

  const handleViewPassword = async (
    config: EmailConfiguration,
  ): Promise<string> => {
    if (!config.secret_id) {
      throw new Error("No secret ID available for this configuration");
    }
    const response = await fetchPortalPassword(config.secret_id);
    const decryptedPassword = await decryptPassword(response.secret_value);
    return decryptedPassword;
  };

  const handleViewPasswordError = () => {
    controlToastState("view_password_error");
  };

  const field1 =
    modalMode === "add"
      ? addModeField1
      : getEditModeField1(selectedConfig?.secret_id ?? "");
  const field2 = modalMode === "add" ? addModeField2 : editModeField2;
  const modalTitle =
    modalMode === "add" ? "Add Email and Password" : "Edit Password";

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-red-500">Error: {error}</span>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 p-2">
      <Header onAddClick={handleAddClick} />

      {emailConfigurations.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-gray-500">
          No email configurations found. Click "Add" to create one.
        </div>
      ) : (
        emailConfigurations.map((config) => (
          <EmailPasswordCard
            key={config.id}
            email={config.email}
            onEdit={() => handleEdit(config)}
            onViewPassword={() => handleViewPassword(config)}
            onViewPasswordError={handleViewPasswordError}
          />
        ))
      )}

      <AddEmailPasswordModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        title={modalTitle}
        modalId="email-password-modal"
        field1={field1}
        field2={field2}
      />

      <Toast
        type="success"
        header="Saved successfully!"
        id="email_password_save_success"
      />
      <Toast
        type="error"
        header="Failed to save. Please try again."
        id="email_password_save_error"
      />
      <Toast
        type="error"
        header="Failed to fetch password. Please try again."
        id="view_password_error"
      />
    </div>
  );
};

export default EmailPasswordConfiguration;
